import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { Selecteur } from '@journal/ui';
import { descriptionAffichable } from './modele.ts';

/**
 * Le traitement de texte de la description d'une phase.
 *
 * Repris de la v1 : gras, italique, souligné, barré, surlignage, couleur,
 * police, taille et listes, sans bibliothèque ajoutée. On s'en tient aux
 * commandes que le navigateur sait faire seul (`execCommand`) : le résultat
 * reste du HTML simple, lisible à l'écran comme à l'impression, et le serveur
 * n'a qu'une liste courte de formes à laisser passer.
 *
 * ── Le contenu n'est JAMAIS piloté par React ────────────────────────────────
 * Il est posé dans la zone à l'ouverture, et lu à l'enregistrement. Le tenir
 * dans un état ferait réécrire la zone à chaque frappe, et le curseur sauterait
 * en fin de texte — la même règle que l'agenda.
 */

export const POLICES = ['Par défaut', 'Arial', 'Georgia', 'Times New Roman', 'Verdana', 'Courier New'];
/** Les tailles d'`execCommand('fontSize')`, de 1 à 7. */
export const TAILLES: ReadonlyArray<[string, string]> = [
  ['Petit', '2'], ['Normal', '3'], ['Grand', '5'], ['Très grand', '6'],
];
const SURLIGNAGE = '#fde68a';
const COULEUR_DEFAUT = '#2f4bd0';
/** Espace sans largeur : l'appui du curseur après un surlignage. */
const INVISIBLE = '​';

/** Ce que la zone contient, ou rien si elle ne porte aucun mot. */
export function lireZone(zone: HTMLElement | null): string {
  if (!zone) return '';
  const html = zone.innerHTML.replace(new RegExp(INVISIBLE, 'g'), '').trim();
  return /^(<br\s*\/?>|<div>(<br\s*\/?>)?<\/div>|\s|&nbsp;)*$/i.test(html) ? '' : html;
}

export function EditeurRiche({
  libelle, valeurInitiale, zone, indication,
}: {
  libelle: string;
  valeurInitiale: string;
  zone: RefObject<HTMLDivElement | null>;
  indication?: string;
}) {
  const idLibelle = useId();
  /*
   * La sélection d'avant le clic sur un outil.
   *
   * Choisir une police dans une liste déroulante retire le focus de la zone :
   * sans cette copie, la police s'appliquerait au curseur remis au début, pas
   * au texte qu'on venait de sélectionner.
   */
  const selection = useRef<Range | null>(null);

  useEffect(() => {
    if (zone.current) zone.current.innerHTML = descriptionAffichable(valeurInitiale);
    // Posé une fois, à l'ouverture : voir l'en-tête.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function memoriser() {
    const sel = window.getSelection();
    const el = zone.current;
    if (!sel || sel.rangeCount === 0 || !el) return;
    const plage = sel.getRangeAt(0);
    if (el.contains(plage.commonAncestorContainer)) selection.current = plage.cloneRange();
  }

  function commande(nom: string, argument?: string) {
    const el = zone.current;
    if (!el) return;
    el.focus();
    if (selection.current) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(selection.current);
    }
    document.execCommand(nom, false, argument);
    memoriser();
  }

  /** L'élément surligné qui contient ce nœud, s'il y en a un dans la zone. */
  function surligneAutourDe(noeud: Node | null): HTMLElement | null {
    const el = zone.current;
    for (let n: Node | null = noeud; n && n !== el; n = n.parentNode) {
      if (n instanceof HTMLElement) {
        const fond = n.style.backgroundColor;
        if (fond && fond !== 'transparent' && fond !== 'rgba(0, 0, 0, 0)') return n;
      }
    }
    return null;
  }

  /*
   * Ce que laisse un surlignage retiré : `background-color: transparent`, sur
   * un `span` qui ne sert plus à rien. On retire la propriété, et le `span`
   * s'il n'a plus de raison d'être — sinon la description se charge de
   * balises vides à chaque aller-retour.
   */
  function nettoyerSurlignagesRetires() {
    for (const span of zone.current?.querySelectorAll<HTMLElement>('[style*="background-color"]') ?? []) {
      const fond = span.style.backgroundColor;
      if (fond !== 'transparent' && fond !== 'rgba(0, 0, 0, 0)' && fond !== 'initial') continue;
      span.style.removeProperty('background-color');
      if (!span.getAttribute('style')) span.removeAttribute('style');
      if (span.tagName === 'SPAN' && span.attributes.length === 0) span.replaceWith(...span.childNodes);
    }
  }

  /**
   * Surligner, ou retirer le surlignage — un interrupteur.
   *
   * Le bouton surlignait à chaque clic, sans jamais retirer : pour enlever un
   * surlignage, il fallait « ✕ », qui efface AUSSI le gras, la couleur et la
   * police. Désormais, un second clic sur un passage surligné le rend normal.
   *
   * Après un surlignage, la frappe qui suit n'est pas surlignée : sans cela,
   * tout ce qu'on écrivait derrière le mot prenait le fond jaune.
   *
   * Le surlignage passe par le fond ; `backColor` prend le relais d'un vieux
   * moteur.
   */
  function surligner() {
    const el = zone.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (selection.current && sel) {
      sel.removeAllRanges();
      sel.addRange(selection.current);
    }
    if (!sel || sel.rangeCount === 0) return;

    const deja = surligneAutourDe(sel.anchorNode) && surligneAutourDe(sel.focusNode);
    // Curseur posé dans un mot surligné, sans sélection : tout le passage.
    if (sel.isCollapsed) {
      const passage = surligneAutourDe(sel.anchorNode);
      if (!passage) return;
      const plage = document.createRange();
      plage.selectNodeContents(passage);
      sel.removeAllRanges();
      sel.addRange(plage);
    }

    document.execCommand('styleWithCSS', false, 'true');
    const couleur = deja ? 'transparent' : SURLIGNAGE;
    if (!document.execCommand('hiliteColor', false, couleur)) {
      document.execCommand('backColor', false, couleur);
    }
    if (!deja) {
      /*
       * La suite s'écrit SANS fond. Chrome garde le curseur à l'intérieur du
       * passage qu'il vient de surligner, quoi qu'on lui dise : on le pose donc
       * hors du passage, derrière un caractère invisible — retiré à la lecture
       * de la zone, il n'est jamais enregistré.
       */
      const plage = sel.getRangeAt(0);
      const passage = surligneAutourDe(plage.endContainer);
      if (passage) {
        const appui = document.createTextNode(INVISIBLE);
        passage.after(appui);
        plage.setStart(appui, 1);
        plage.collapse(true);
      } else {
        plage.collapse(false);
      }
      sel.removeAllRanges();
      sel.addRange(plage);
    }
    document.execCommand('styleWithCSS', false, 'false');
    nettoyerSurlignagesRetires();
    memoriser();
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span id={idLibelle} className="text-[13px] font-medium text-[var(--color-fg)]">{libelle}</span>
      <div
        role="toolbar"
        aria-label="Mise en forme de la description"
        className={[
          'flex flex-wrap items-center gap-1 rounded-t-lg border border-b-0',
          'border-[var(--color-border-strong)] bg-[var(--color-surface-raised)] px-1.5 py-1',
        ].join(' ')}
      >
        <Outil titre="Gras" surClic={() => commande('bold')}><strong>G</strong></Outil>
        <Outil titre="Italique" surClic={() => commande('italic')}><em className="font-serif">I</em></Outil>
        <Outil titre="Souligné" surClic={() => commande('underline')}><u>S</u></Outil>
        <Outil titre="Barré" surClic={() => commande('strikeThrough')}><s>A</s></Outil>
        <Separateur />
        <Outil titre="Surligner" surClic={surligner}>
          <span className="rounded-sm px-1" style={{ background: SURLIGNAGE }}>ab</span>
        </Outil>
        <label
          title="Couleur du texte"
          className="relative inline-flex h-7 w-8 cursor-pointer items-center justify-center rounded-md hover:bg-[var(--color-surface-hover)]"
        >
          <span className="sr-only">Couleur du texte</span>
          <span aria-hidden="true" className="text-[13px] font-bold underline decoration-[3px]" style={{ textDecorationColor: COULEUR_DEFAUT }}>A</span>
          <input
            type="color"
            defaultValue={COULEUR_DEFAUT}
            onMouseDown={memoriser}
            onInput={(e) => commande('foreColor', (e.target as HTMLInputElement).value)}
            className="absolute inset-0 size-full cursor-pointer opacity-0"
          />
        </label>
        <Separateur />
        <div className="w-32" onMouseDown={memoriser}>
          <Selecteur
            libelle="Police"
            libelleMasque
            taille="sm"
            value=""
            options={[{ valeur: '', texte: 'Police' }, ...POLICES.map((p) => ({ valeur: p, texte: p }))]}
            onChange={(e) => {
              const nom = e.target.value;
              if (nom) commande('fontName', nom === 'Par défaut' ? 'inherit' : nom);
            }}
          />
        </div>
        <div className="w-28" onMouseDown={memoriser}>
          <Selecteur
            libelle="Taille"
            libelleMasque
            taille="sm"
            value=""
            options={[{ valeur: '', texte: 'Taille' }, ...TAILLES.map(([n, v]) => ({ valeur: v, texte: n }))]}
            onChange={(e) => { if (e.target.value) commande('fontSize', e.target.value); }}
          />
        </div>
        <Separateur />
        <Outil titre="Liste à puces" surClic={() => commande('insertUnorderedList')}>•≡</Outil>
        <Outil titre="Liste numérotée" surClic={() => commande('insertOrderedList')}>1≡</Outil>
        <Separateur />
        <Outil titre="Enlever la mise en forme" surClic={() => commande('removeFormat')}>✕</Outil>
      </div>
      {/*
        * `suppressContentEditableWarning` : React prévient qu'un nœud éditable
        * dont il tiendrait les enfants serait réécrit sous les doigts. Il n'en
        * tient aucun — le contenu est posé par l'effet d'ouverture.
        */}
      <div
        ref={zone}
        role="textbox"
        aria-multiline="true"
        aria-labelledby={idLibelle}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        data-vide={indication}
        onKeyUp={memoriser}
        onMouseUp={memoriser}
        onBlur={memoriser}
        onPaste={(e) => {
          /*
           * Collage en TEXTE BRUT : un copier-coller depuis un traitement de
           * texte amènerait ses polices, ses tailles et ses couleurs, et la fiche
           * perdrait toute unité. Le serveur les écarterait de toute façon.
           */
          e.preventDefault();
          document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
        }}
        className={[
          'fp-riche min-h-40 max-h-[50vh] overflow-y-auto rounded-b-lg border px-3 py-2.5',
          'border-[var(--color-border-strong)] bg-[var(--color-surface-input)]',
          'text-sm leading-relaxed text-[var(--color-fg)] outline-none',
          'focus-visible:ring-2 focus-visible:ring-[var(--color-focus)]',
        ].join(' ')}
      />
    </div>
  );
}

/**
 * Un outil de la barre.
 *
 * `onMouseDown` empêché : sans cela, presser le bouton retire le focus de la
 * zone AVANT que la commande ne s'applique, et elle ne s'appliquerait à rien.
 */
function Outil({
  titre, surClic, children,
}: {
  titre: string;
  surClic: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={titre}
      aria-label={titre}
      onMouseDown={(e) => e.preventDefault()}
      onClick={surClic}
      className={[
        'inline-flex h-7 min-w-8 items-center justify-center rounded-md px-1.5',
        'text-[13px] text-[var(--color-fg)] hover:bg-[var(--color-surface-hover)]',
        'focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Separateur() {
  return <span aria-hidden="true" className="mx-0.5 h-5 w-px bg-[var(--color-border)]" />;
}
